import { FormProvider } from "react-hook-form";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { DefaultInputField } from "../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { DefaultFormSelectField } from "../../../../common/components/forms/inputs/dropdown/DefaultFormSelectField";
import { BarcodeField } from "../../../../common/components/forms/inputs/BarcodeField/BarcodeField";
import { UnitOfMeasure } from "../../../../common/enums";
import { useTransferQuickAddProduct } from "../hooks/useTransferQuickAddProduct";
import { IInventoryItemSearchResultModel } from "../models/inventoryTransferModels";
import styles from "./InventoryTransferQuickAddProductDialog.module.scss";

interface IQuickAddDialogProps {
  visible: boolean;
  onHide: () => void;
  onCreated: (product: IInventoryItemSearchResultModel) => void;
}

const UOM_OPTIONS = [
  { id: UnitOfMeasure.Piece, name: "Брой" },
  { id: UnitOfMeasure.Gram, name: "Грамаж" },
];

/**
 * Minimal "Quick add product" form stacked over the transfer dialog.
 * InitialQuantity is hardcoded to 0 — the transfer itself is expected
 * to bring the stock via its lines.
 */
export const InventoryTransferQuickAddProductDialog = ({
  visible,
  onHide,
  onCreated,
}: IQuickAddDialogProps) => {
  const { form, categories, submit, isSubmitting } = useTransferQuickAddProduct({
    onCreated: (product) => {
      onCreated(product);
      onHide();
    },
  });

  const categoryOptions = categories.map((c) => ({
    id: c.id,
    name: c.emoji ? `${c.emoji} ${c.name}` : c.name,
  }));

  return (
    <AppDialog visible={visible} header="Нов продукт" onHide={onHide} style={{ width: "min(560px, 96vw)" }}>
      <FormProvider {...form}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className={styles.form}
        >
          <DefaultFormSelectField
            name="categoryId"
            label="Категория"
            required
            options={categoryOptions}
            width="100%"
          />
          <DefaultInputField
            name="name"
            label="Име"
            placeholder="напр. Isotonic"
            type="text"
            width="100%"
            required
          />
          <DefaultInputField
            name="brand"
            label="Марка"
            placeholder="Незадължително"
            type="text"
            width="100%"
          />
          <BarcodeField
            name="barcode"
            label="Баркод"
            placeholder="Незадължително"
            width="100%"
          />
          <div className={styles.grid}>
            <DefaultInputField
              name="price"
              label="Цена"
              placeholder="0.00"
              type="number"
              min={0}
              step={0.01}
              width="100%"
              required
            />
            <DefaultInputField
              name="costPrice"
              label="Себестойност"
              placeholder="0.00"
              type="number"
              min={0}
              step={0.01}
              width="100%"
              required
            />
          </div>
          <div className={styles.grid}>
            <DefaultFormSelectField
              name="unitOfMeasure"
              label="Мярка"
              options={UOM_OPTIONS}
              filter={false}
              includeId={false}
              width="100%"
              required
            />
            <DefaultInputField
              name="lowStockThreshold"
              label="Праг за ниска наличност"
              placeholder="5"
              type="number"
              min={0}
              step={0.001}
              width="100%"
              required
            />
          </div>

          <p className={styles.hint}>
            Началното количество се задава на 0 — самата доставка ще зареди продукта.
          </p>

          <div className={styles.actions}>
            <DefaultButton
              label="Отказ"
              design="outline"
              onClick={onHide}
              type="button"
              width="120px"
            />
            <DefaultButton
              label="Създай"
              type="submit"
              loading={isSubmitting}
              width="120px"
            />
          </div>
        </form>
      </FormProvider>
    </AppDialog>
  );
};
